const ACTIVE_STATUSES = ['queued', 'running'];
const TERMINAL_STATUSES = ['completed', 'completed_with_failures', 'failed'];
const POLL_INTERVAL_MS = 5000;
const REQUEST_TIMEOUT_MS = 10000;

/**
 * Keeps the administrator's run card current using stored progress only.
 * @param {Object} options browser dependencies injectable for isolated tests.
 * @returns {Object} Alpine state and lifecycle actions.
 */
export function adminAppleVideoEligibilityRefresh(options = {}) {
  const fetcher = options.fetch || window.fetch.bind(window);
  const documentRef = options.document || document;
  const setTimer = options.setTimeout || window.setTimeout.bind(window);
  const clearTimer = options.clearTimeout || window.clearTimeout.bind(window);
  const AbortControllerClass = options.AbortController || window.AbortController;

  return {
    status: '',
    statusUrl: '',
    progressHtml: '',
    submitting: false,
    loading: false,
    stopped: false,
    destroyed: false,
    error: '',
    pollTimer: null,
    requestTimer: null,
    requestController: null,
    visibilityHandler: null,
    navigationHandler: null,

    /** @returns {boolean} whether another request must be prevented. */
    get busy() {
      return this.submitting || ACTIVE_STATUSES.includes(this.status);
    },

    /** Initializes from Rails markup, including active runs opened in a new tab. */
    init() {
      this.status = this.$el.dataset.initialStatus || '';
      this.statusUrl = this.$el.dataset.statusUrl;
      this.progressHtml = this.$refs.progress.innerHTML;
      this.visibilityHandler = this.visibilityChanged.bind(this);
      // Alpine 3.9 removes x-data scope before invoking the destroy hook.
      this.destroy = this.destroy.bind(this);
      this.navigationHandler = this.destroy;
      documentRef.addEventListener('visibilitychange', this.visibilityHandler);
      documentRef.addEventListener('turbo:before-cache', this.navigationHandler);
      this.schedulePoll();
    },

    /** Locks the native POST immediately; Rails retains its redirect and flash behavior. */
    submit(event) {
      if (this.busy) {
        event.preventDefault();
        return;
      }
      this.submitting = true;
      if (event.submitter) event.submitter.disabled = true;
    },

    /** Reads one fresh snapshot without overlapping requests or replacing the action. */
    async poll() {
      if (this.destroyed || this.stopped || this.loading || documentRef.hidden || !ACTIVE_STATUSES.includes(this.status)) return;
      this.clearPoll();
      this.loading = true;
      this.requestController = new AbortControllerClass();
      this.requestTimer = setTimer(() => this.requestController?.abort(), REQUEST_TIMEOUT_MS);

      try {
        const response = await fetcher(this.statusUrl, {
          headers: { Accept: 'application/json' }, credentials: 'same-origin',
          cache: 'no-store', redirect: 'error', signal: this.requestController.signal,
        });
        if (this.destroyed) return;
        if (response.status === 401 || response.status === 403 || response.redirected) {
          this.stopped = true;
          this.error = 'Live updates stopped. Reload this page and sign in again.';
          return;
        }
        if (!response.ok) throw new Error('Status request failed');
        const payload = await response.json();
        if (this.destroyed) return;
        if (![...ACTIVE_STATUSES, ...TERMINAL_STATUSES].includes(payload.status) || typeof payload.html !== 'string' || !payload.html.trim()) {
          throw new Error('Invalid run status');
        }
        this.status = payload.status;
        this.progressHtml = payload.html;
        this.error = '';
      } catch (_error) {
        if (!this.destroyed) this.error = 'Live updates are delayed. Retrying automatically; the last confirmed result is shown.';
      } finally {
        clearTimer(this.requestTimer);
        this.requestTimer = null;
        this.requestController = null;
        this.loading = false;
        this.schedulePoll();
      }
    },

    /** Pauses in hidden tabs and immediately refreshes active work on return. */
    async visibilityChanged() {
      if (documentRef.hidden) this.clearPoll();
      else await this.poll();
    },

    /** Schedules only after the previous response, including transient failures. */
    schedulePoll() {
      this.clearPoll();
      if (this.destroyed || this.stopped || documentRef.hidden || !ACTIVE_STATUSES.includes(this.status)) return;
      this.pollTimer = setTimer(() => {
        this.pollTimer = null;
        return this.poll();
      }, POLL_INTERVAL_MS);
    },

    /** Removes the next scheduled poll, if any. */
    clearPoll() {
      if (this.pollTimer !== null) clearTimer(this.pollTimer);
      this.pollTimer = null;
    },

    /** Releases timers/listeners and prevents late responses from changing a discarded page. */
    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.clearPoll();
      if (this.requestTimer !== null) clearTimer(this.requestTimer);
      this.requestController?.abort();
      documentRef.removeEventListener('visibilitychange', this.visibilityHandler);
      documentRef.removeEventListener('turbo:before-cache', this.navigationHandler);
    },
  };
}

document.addEventListener('alpine:init', () => {
  Alpine.data('admin_apple_video_eligibility_refresh', () => adminAppleVideoEligibilityRefresh());
});
